import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Col, Row, Space, Spin, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { api, endpoints, upload } from '../../api';
import { EditSvg, RemoveSvg } from '../../icons';
import { routerPath } from '../../router/routerPath';
import { authContext } from '../../store/authStore/AuthContext';
import {
  BlueLayout,
  CustomButton,
  CustomInfoLayout,
  DownloadCol,
  FormBlock,
  FormContainer,
  FormInput,
  PagesContainerUI,
} from '../../styles/styled';
import { slugRegExp } from '../../utils/patterns';
import { UploadComponent } from '../NewApplicationPage/components/BookRegistrationComponent/components/BookForm/UploadComponent';
import { FileEditorData } from '../NewApplicationPage/components/BookRegistrationComponent/components/BookForm/UploadComponent/UploadComponent';
import { FileDataType, FileRequestType } from '../NewApplicationPage/types';
import { DocumentCategoriesType, RemoveDocumentType } from './types';

type FileJsonNamesType = 'file_application_instruction' | 'file_personal_data_approval';

type LoadingFilesType = FileJsonNamesType[];

type FileTypes = {
  file_application_instruction?: FileRequestType;
  file_personal_data_approval?: FileRequestType;
};

export const DocumentCategoryPage = () => {
  const { state } = useContext(authContext);
  const navigate = useNavigate();
  const [data, setData] = useState<FileTypes>();
  const [documentCategoriesState, setDocumentCategories] = useState<DocumentCategoriesType>([]);
  const [loadingFiles, setLoadingFiles] = useState<LoadingFilesType>([]);
  const [removeDocument, setRemoveDocument] = useState<RemoveDocumentType>({ remove: false, ids: [] });

  const escFunction = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<DocumentCategoriesType>(endpoints.getDocumentCategories());
        setDocumentCategories(response.data);
      } catch (err) {}
    })();
  }, []);

  useEffect(() => {
    state.isAdmin &&
      (async () => {
        try {
          const response = await api.get(endpoints.getFileBySlug('file_application_instruction'));
          setData((prevState) => ({ ...prevState, file_application_instruction: response.data }));
        } catch {
          return;
        }
      })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    state.isAdmin &&
      (async () => {
        try {
          const response = await api.get(endpoints.getFileBySlug('file_personal_data_approval'));
          setData((prevState) => ({ ...prevState, file_personal_data_approval: response.data }));
        } catch {
          return;
        }
      })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (removeDocument.remove && removeDocument.ids.length > 0) {
      (async () => {
        const response = await api.delete(endpoints.deleteDocumentById({ id: removeDocument.ids[0] }));

        if (response) {
          setDocumentCategories(documentCategoriesState.filter((el) => el.id !== removeDocument.ids[0]));
          setRemoveDocument({ ...removeDocument, ids: removeDocument.ids.splice(1, removeDocument.ids.length) });
        }

        if (removeDocument.ids.length === 0) setRemoveDocument({ ...removeDocument, remove: false });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeDocument]);

  const uploadFiles = useCallback(
    async (fileData: FileDataType, fileType: FileJsonNamesType, slug: string) => {
      setLoadingFiles((prevState) => [...prevState, fileType]);

      try {
        const response = await upload.post<{ success: boolean; file_id: number }>(endpoints.addFile(), {
          ...fileData,
          ext: fileData.title.split('.').pop(),
          slug: `${slug}`,
        });

        setLoadingFiles((prevState) => prevState.filter((el) => el !== fileType));

        setData((prevState) => ({
          ...prevState,
          [fileType]: { id: response.data.file_id, title: fileData.title },
        }));
      } catch (err) {
        message.error('Произошла ошибка при загрузке файла');
        setLoadingFiles((prevState) => prevState.filter((el) => el !== fileType));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const beforeUploadFile = async (data: FileEditorData, fileType: FileJsonNamesType, slug: string) => {
    slugRegExp.test(slug)
      ? await uploadFiles(
          {
            base64: data.data.split('base64,')[1],
            title: data.name as string,
            ext: data.type as string,
          },
          fileType,
          slug,
        )
      : null;
  };

  const removeFileInstruction = async () => {
    if (data && data.file_application_instruction) {
      await api.delete(endpoints.deleteFile(data.file_application_instruction.id));

      setData((prevState) => {
        return { ...prevState, file_application_instruction: undefined };
      });
    }
  };

  const removeFileApproval = async () => {
    if (data && data.file_personal_data_approval) {
      await api.delete(endpoints.deleteFile(data.file_personal_data_approval.id));

      setData((prevState) => {
        return { ...prevState, file_personal_data_approval: undefined };
      });
    }
  };

  return (
    <PagesContainerUI>
      <BlueLayout>Документы</BlueLayout>
      <FormBlock none={!state.isAdmin} white>
        <FormContainer>
          <Row gutter={[20, 20]}>
            <DownloadCol span={24} style={{}}>
              <CustomInfoLayout>Инструкция по подаче заявления на экспертизу учебника</CustomInfoLayout>
              <Space align="center">
                {loadingFiles.includes('file_application_instruction') ? (
                  <Spin />
                ) : (
                  <UploadComponent
                    onClear={() => removeFileInstruction()}
                    value={data?.file_application_instruction}
                    formats={['.pdf', '.doc', '.docx']}
                    onChange={(data) =>
                      beforeUploadFile(data, 'file_application_instruction', 'file_application_instruction')
                    }
                  />
                )}
              </Space>
              <p>рекомендуется прикреплять файл в формате .pdf</p>
            </DownloadCol>
            <DownloadCol span={24} style={{ border: 0 }}>
              <CustomInfoLayout>Согласие на обработку персональных данных</CustomInfoLayout>
              <Space align="center">
                {loadingFiles.includes('file_personal_data_approval') ? (
                  <Spin />
                ) : (
                  <UploadComponent
                    onClear={() => removeFileApproval()}
                    value={data?.file_personal_data_approval}
                    formats={['.pdf', '.doc', '.docx']}
                    onChange={(data) =>
                      beforeUploadFile(data, 'file_personal_data_approval', 'file_personal_data_approval')
                    }
                  />
                )}
              </Space>
              <p>рекомендуется прикреплять файл в формате .pdf</p>
            </DownloadCol>
          </Row>
        </FormContainer>
      </FormBlock>
      <FormBlock white>
        <FormContainer>
          {documentCategoriesState.length === 0 && <Spin />}
          {documentCategoriesState.map((el) => {
            return (
              <Row key={el.id} gutter={[15, 20]} align="middle">
                <Col span={1}>
                  <div className="numeration">{el.sort}</div>
                </Col>
                <Col span={3}>
                  <FormInput value={el.sort} />
                </Col>
                <Col span={16}>
                  <div className="dark-blue">{el.title}</div>
                </Col>
                <Col className="svg-icons right-side-block" span={4}>
                  <Link to={`${routerPath.editDocumentCategory}${el.id}`}>
                    <EditSvg />
                  </Link>
                  <button
                    className="clickable-svg"
                    onClick={() => {
                      setDocumentCategories(documentCategoriesState.filter((e) => e.id !== el.id));
                      setRemoveDocument({
                        ...removeDocument,
                        ids: [...removeDocument.ids.filter((e) => e !== el.id), el.id],
                      });
                    }}
                  >
                    <RemoveSvg />
                  </button>
                </Col>
              </Row>
            );
          })}
          <div className="center">
            <CustomButton onClick={() => setRemoveDocument({ ...removeDocument, remove: true })}>
              Сохранить
            </CustomButton>
          </div>
          <br />
          <CustomButton onClick={() => navigate(routerPath.newDocumentCategory, { replace: true })} width="300px" lined>
            Добавить раздел
          </CustomButton>
        </FormContainer>
      </FormBlock>
    </PagesContainerUI>
  );
};
